import { isInvalidDollarInput } from '../../utils/formatters';

/**
 * When the manage-load dialog's primary button is allowed to fire, and why it
 * isn't when it isn't.
 *
 * This lives outside the component because it is the single most
 * failure-prone rule in the dialog and the hardest to see from the UI: a
 * disabled button looks identical whichever condition produced it. Keeping it
 * pure means the combinations can be asserted directly instead of inferred
 * from a greyed-out control.
 *
 * The distinction that matters is approving versus updating. Approving a load
 * for the first time has to establish everything dispatch depends on, none of
 * which has a default yet. Updating a load that is already approved must not
 * re-demand any of it — those values are already stored, and the update route
 * falls back to the saved row for each field it is not sent. Asking for them
 * again is what silently greyed out the button on loads whose fulfilment
 * method no longer resolved against their delivery address, blocking edits as
 * ordinary as a pickup date or a note.
 */

export interface SaveGateForm {
  locationId: string;
  deliveryAddressId: string;
  loadOptionId: string;
  palletSizeId: string;
  poNumber: string;
  priceOverrideInput: string;
  deliveryFeeInput: string;
  carrierRateInput: string;
  pickupDate: string;
  nextStatus: string;
}

export interface SaveGateContext {
  /** False only for a load still PENDING or WAITLIST. */
  isApprovedAlready: boolean;
  /** Cleared while a save or reject is in flight, and for cancelled loads. */
  canEdit: boolean;
  isProductDeleted: boolean;
  isPallet: boolean;
  /** The selected Ship To sub-location mandates a PO number. */
  requiresPoNumber: boolean;
  /** The customer was switched, so a new Ship To (owned by it) must be chosen. */
  customerChanged?: boolean;
}

/** A customer switch with no new Ship To chosen yet is never saveable. */
export function isCustomerShipToMissing(form: SaveGateForm, ctx: SaveGateContext): boolean {
  return ctx.customerChanged === true && !form.deliveryAddressId;
}

/** A PO-required sub-location has no PO entered yet. */
export function isPoNumberMissing(form: SaveGateForm, ctx: SaveGateContext): boolean {
  return ctx.requiresPoNumber && !form.poNumber.trim();
}

// Marking a load ready for pickup needs a pickup date — the deadline + email are
// derived from it, and the server rejects the save without one.
export function isPickupDateMissingForReadyForPickup(form: SaveGateForm): boolean {
  return form.nextStatus === 'READY_FOR_PICKUP' && !form.pickupDate.trim();
}

/** A malformed amount is never saveable, whatever the load's status. */
export function hasInvalidMoney(form: SaveGateForm): boolean {
  return (
    isInvalidDollarInput(form.priceOverrideInput) ||
    isInvalidDollarInput(form.deliveryFeeInput) ||
    isInvalidDollarInput(form.carrierRateInput)
  );
}

/**
 * First-time approval: warehouse, Ship To, fulfilment method and — for a
 * palletised product — the pallet size all have to be chosen before the load
 * can go anywhere.
 */
export function isApproveDisabled(form: SaveGateForm, ctx: SaveGateContext): boolean {
  return (
    !ctx.canEdit ||
    ctx.isProductDeleted ||
    !form.locationId ||
    !form.deliveryAddressId ||
    !form.loadOptionId ||
    (ctx.isPallet && !form.palletSizeId) ||
    isPoNumberMissing(form, ctx) ||
    hasInvalidMoney(form)
  );
}

/**
 * Editing a load that is already approved.
 *
 * `locationId` stays required because the update route rejects a request
 * without one outright. Everything else the approval checklist asks for is
 * deliberately absent.
 */
export function isUpdateDisabled(form: SaveGateForm, ctx: SaveGateContext): boolean {
  return (
    !ctx.canEdit ||
    ctx.isProductDeleted ||
    !form.locationId ||
    isCustomerShipToMissing(form, ctx) ||
    isPoNumberMissing(form, ctx) ||
    hasInvalidMoney(form)
  );
}

export function isSaveDisabled(form: SaveGateForm, ctx: SaveGateContext): boolean {
  return ctx.isApprovedAlready ? isUpdateDisabled(form, ctx) : isApproveDisabled(form, ctx);
}

/** What the user has staged that the primary button might need to save. */
export interface StagedWork {
  statusChanged: boolean;
  carrierChanged: boolean;
  isBusy: boolean;
}

/**
 * `isSaveDisabled` plus the two exemptions that let a read-only load still be
 * acted on: a changed status (cancel/reject a load missing a fulfilment method)
 * and a staged carrier (CUSTOMER_CANCELLED and REJECTED have `canEdit` false).
 */
export function isPrimaryActionDisabled(
  form: SaveGateForm,
  ctx: SaveGateContext,
  staged: StagedWork
): boolean {
  if (staged.isBusy) return true;
  // A staged customer switch with no new Ship To can never be saved, even when a
  // status/carrier change would otherwise exempt the load — the server rejects it.
  if (isCustomerShipToMissing(form, ctx)) return true;
  // Likewise ready-for-pickup without a pickup date: the status change would
  // otherwise exempt it, but the server rejects it.
  if (isPickupDateMissingForReadyForPickup(form)) return true;
  if (staged.statusChanged || staged.carrierChanged) return false;
  return isSaveDisabled(form, ctx);
}

/**
 * Why the button is disabled, for its tooltip.
 *
 * Empty string when it is enabled, or when the cause is a save already in
 * flight — the button's own label already says so.
 */
export function saveDisabledReason(form: SaveGateForm, ctx: SaveGateContext): string {
  if (ctx.canEdit && isPickupDateMissingForReadyForPickup(form)) {
    return 'Set a pickup date before marking this load ready for pickup';
  }
  if (!isSaveDisabled(form, ctx)) return '';
  if (!ctx.canEdit) return '';
  if (ctx.isProductDeleted) return 'This load’s product has been deleted';
  if (isCustomerShipToMissing(form, ctx)) return 'Select a Ship To for the new customer';
  if (hasInvalidMoney(form)) return 'Fix the highlighted amounts first';
  if (!form.locationId) return 'Select a pickup warehouse first';
  if (isPoNumberMissing(form, ctx)) return 'This sub-location requires a PO number';
  if (ctx.isApprovedAlready) return '';
  return 'Set the warehouse, Ship To, and fulfilment method first';
}
